import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IResourceItemType } from '@/api/types';

import {
  ResourceTableCell,
  ResourceTableHeader,
  ResourceTableHeaderCell,
  ResourceTableRow,
  Table,
} from '@/components/common';
import { getLabel, getResourceItemId } from '@/helpers';
import { ChevronRight } from '@/assets/icons';
import { useAppDispatch, useTimezone } from '@/hooks';
import { openPreview } from '@/store/slices/resourcesSlice';
import { MONTH_DAY_YEAR_FORMAT } from '@/config/variables';
import { ResourcesNoData } from '.';
import { ButtonPreview, Container, HeaderContainer, Title } from './ResourcesItemsCard.styled';

interface IProps {
  title: string;
  noDataLabel: string;
  iconSrc: string;
  button: React.ReactNode;
  items: IResourceItemType[];
}

const ResourcesItemsCard = ({ title, iconSrc, button, items, noDataLabel }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();
  const dispatch = useAppDispatch();

  return (
    <Container>
      <HeaderContainer>
        <Title>
          <img src={iconSrc} />
          <h4>{title}</h4>
        </Title>
        {button}
      </HeaderContainer>
      {items.length === 0 ? (
        <ResourcesNoData label={noDataLabel} />
      ) : (
        <Table>
          <ResourceTableHeader className="small">
            <ResourceTableHeaderCell size={2}>{t`resources.foldersList.headerName`}</ResourceTableHeaderCell>
            <ResourceTableHeaderCell size={1}>{t`resources.foldersList.headerPostedDate`}</ResourceTableHeaderCell>
            <ResourceTableHeaderCell width="12px" minWidth="12px" className="p-0 ms-3"></ResourceTableHeaderCell>
          </ResourceTableHeader>
          {items.map(item => (
            <ResourceTableRow className="small" key={getResourceItemId(item)}>
              <ResourceTableCell size={2}>{getLabel(item)}</ResourceTableCell>
              <ResourceTableCell size={1}>{formatDateTime(item.dateStart, MONTH_DAY_YEAR_FORMAT)}</ResourceTableCell>
              <ResourceTableCell width="12px" minWidth="12px">
                <ButtonPreview src={ChevronRight} onClick={() => dispatch(openPreview(item))} />
              </ResourceTableCell>
            </ResourceTableRow>
          ))}
        </Table>
      )}
    </Container>
  );
};

export { ResourcesItemsCard };
